import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Modal, DashboardProgressBar } from "../../components";
import type { SelectOption } from "../../components";
import {
  LoanApplication,
  useLoanDashboard,
  useSDKContext,
  useLoanApplication,
} from "../../contexts";
import { formatAmount, formatISOString } from "../../utils";
import {
  LoanApplicationStatus,
  LoanProductType,
  StatusLabelReverseMapping,
} from "../../hooks/api/types";
import {
  ButtonLoaderIcon,
  CoinsIcon,
  EmptyApplicationIcon,
  HandCoinsIcon,
  HeavyWarningIcon,
  PenIcon,
  PlusIcon,
  SearchIcon,
  StatusFilterIcon,
} from "../../assets/icons";
import {
  useGetActiveLoanApplication,
  useLoansSummary,
  useCancelActiveLoanApplication,
} from "../../hooks";
import LoanCard from "./LoanCard";
import { usePostHog } from "posthog-js/react";
import { useDpConfig } from "../../contexts/dpConfig";
import { getStepperStage, useStepper } from "../LoanApplicationSteps/Stepper";
import { datadogRum } from "@datadog/browser-rum";
import StartNewApplicationConfirmModal from "../../views/LoanApplicationSteps/Onboarding/ExistingLoanApplicationModal";
import PortfolioCard from "./LoanCard/PortfolioCard";
import { Select } from "../../components/Select";
import { ACTIVE_STATUSES } from "./LineOfCredit/constants";

const isLoanActive = (loan: LoanApplication) =>
  loan.status === LoanApplicationStatus.ACTIVE ||
  loan.status === LoanApplicationStatus.PARTIALLY_DISBURSED ||
  (loan.caseStatus != null && ACTIVE_STATUSES.includes(loan.caseStatus));

const sortLoansActiveFirst = (loanList: LoanApplication[]) =>
  [...loanList].sort((a, b) => {
    const aActive = isLoanActive(a) ? 0 : 1;
    const bActive = isLoanActive(b) ? 0 : 1;
    return aActive - bActive;
  });

type LoanDashboardFormCardPros = {
  label?: string;
  children?: ReactNode;
};

export const LoanDashboardFormCard: React.FC<LoanDashboardFormCardPros> = ({
  label,
  children,
}) => (
  <div className="cx-grow">
    <div className="cx-px-4">
      <div className="cx-grow cx-text-[#666] cx-font-medium cx-text-base cx-text-center">
        {label}
      </div>
      {children}
    </div>
  </div>
);

type Props = {};

const LoanDashboardForm: React.FC<Props> = () => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [allowNewLoan, setAllowNewLoan] = useState({
    allow: false,
    existing: false,
  });
  const [activeLoanApplication, setActiveLoanApplication] =
    useState<LoanApplication>();
  const [activeLoanContractsSummary, setActiveLoanContractsSummary] = useState({
    totalAmountDisbursed: 0,
    totalOutstandingAmount: 0,
    overdueInstallmentAmount: 0,
    overdueInstallmentPrincipal: 0,
    overdueInstallmentInterest: 0,
    overdueInstallmentLateFee: 0,
    overdueDaysCount: 0,
    upcomingInstallmentsCount: 0,
    upcomingInstallmentsAmount: 0,
    upcomingInvoicesCount: 0,
    upcomingPaymentDate: "",
    creditLineFacilitiesCount: 0,
    termLoanFacilitiesCount: 0,
    totalApprovedFacilityAmount: 0,
    creditLineTotalApprovedAmount: 0,
    creditLineTotalUtilisedAmount: 0,
    creditLineTotalOutstandingAmount: 0,
    termLoanTotalApprovedAmount: 0,
    termLoanTotalOutstandingAmount: 0,
  });
  const {
    state: { loans, loading, filterLoading },
    actions: { refetch },
  } = useLoanDashboard();
  const termFacilitiesRef = useRef<HTMLDivElement>(null);
  const prevFilterLoadingRef = useRef(false);
  const { currentStep, steps, gotoStep, reset } = useStepper();
  const [isStartNewConfirmModalOpen, setIsStartNewConfirmModalOpen] =
    useState(false);

  const { switchDisplayMode } = useSDKContext();
  const {
    mutateAsync: getActiveLoanApplication,
    isLoading: activeApplicationLoading,
  } = useGetActiveLoanApplication();

  const posthog = usePostHog();

  const { mutateAsync: getLoansSummary, isLoading } = useLoansSummary();
  const { mutateAsync: cancelLoanApplicationAPI } =
    useCancelActiveLoanApplication();
  const { actions: loanApplicationActions } = useLoanApplication();

  const {
    state: { uiLocalizationData, settings },
  } = useDpConfig();

  useEffect(() => {
    getActiveLoanApplication().then((data) => {
      setActiveLoanApplication(data);
    });
  }, []);

  const readyForDisplay = useMemo(
    () => !loading && !activeApplicationLoading,
    [loading, activeApplicationLoading]
  );

  useEffect(() => {
    if (
      activeLoanApplication?.id &&
      activeLoanApplication?.stage &&
      readyForDisplay
    ) {
      const [stepName, subStepIndex] = getStepperStage(activeLoanApplication);
      const stepIndex = steps.findIndex((step) => step.name === stepName) || 1;
      gotoStep([stepIndex, subStepIndex]);
    } else if (!activeLoanApplication?.id && readyForDisplay) {
      reset();
    }
  }, [
    activeLoanApplication?.id,
    activeLoanApplication?.stage,
    readyForDisplay,
  ]);

  useEffect(() => {
    getLoansSummary()
      .then((res) => {
        setAllowNewLoan({
          existing: res?.flags?.continueExistingLoanApplication ?? false,
          allow: res?.flags?.allowNewLoanApplication ?? false,
        });
        setActiveLoanContractsSummary({
          totalAmountDisbursed: res?.totalAmountDisbursed,
          totalOutstandingAmount: res?.totalOutstandingAmount,
          overdueInstallmentAmount: res?.overdueInstallmentAmount,
          overdueInstallmentPrincipal: res?.overdueInstallmentPrincipal,
          overdueInstallmentInterest: res?.overdueInstallmentInterest,
          overdueInstallmentLateFee: res?.overdueInstallmentLateFee,
          overdueDaysCount: res?.overdueDaysCount ?? 0,
          upcomingInstallmentsCount: res?.upcomingInstallmentsCount,
          upcomingInstallmentsAmount: res?.upcomingInstallmentsAmount,
          upcomingInvoicesCount: res?.upcomingInvoicesCount ?? 0,
          upcomingPaymentDate: res?.upcomingPaymentDate ?? "",
          creditLineFacilitiesCount: res?.creditLineFacilitiesCount,
          termLoanFacilitiesCount: res?.termLoanFacilitiesCount,
          totalApprovedFacilityAmount: res?.totalApprovedFacilityAmount,
          creditLineTotalApprovedAmount: res?.creditLineTotalApprovedAmount,
          creditLineTotalUtilisedAmount: res?.creditLineTotalUtilisedAmount,
          creditLineTotalOutstandingAmount:
            res?.creditLineTotalOutstandingAmount ?? 0,
          termLoanTotalApprovedAmount: res?.termLoanTotalApprovedAmount ?? 0,
          termLoanTotalOutstandingAmount:
            res?.termLoanTotalOutstandingAmount ?? 0,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const utilisationPercentage = useMemo(() => {
    if (
      !activeLoanContractsSummary.creditLineTotalApprovedAmount ||
      !activeLoanContractsSummary.creditLineTotalUtilisedAmount
    ) {
      return 0;
    }
    return Math.round(
      ((activeLoanContractsSummary.creditLineTotalUtilisedAmount /
        activeLoanContractsSummary.creditLineTotalApprovedAmount) *
        100 *
        100) /
        100
    );
  }, [activeLoanContractsSummary]);

  const getUtilisationColors = (percentage: number) => {
    if (percentage <= 10) {
      return {
        bg: "cx-bg-green-50",
        border: "cx-border-green-200",
        text: "cx-text-green-700",
        dot: "cx-bg-green-500",
      };
    } else if (percentage <= 50) {
      return {
        bg: "cx-bg-orange-50",
        border: "cx-border-orange-200",
        text: "cx-text-orange-700",
        dot: "cx-bg-orange-500",
      };
    } else if (percentage <= 80) {
      return {
        bg: "cx-bg-red-50",
        border: "cx-border-red-200",
        text: "cx-text-red-700",
        dot: "cx-bg-red-500",
      };
    } else {
      return {
        bg: "cx-bg-red-50",
        border: "cx-border-red-200",
        text: "cx-text-red-700",
        dot: "cx-bg-red-500",
      };
    }
  };

  const utilisationColors = useMemo(
    () => getUtilisationColors(utilisationPercentage),
    [utilisationPercentage]
  );

  const handleConfirmStartNewApplication = () => {
    posthog.capture(
      `User confirmed starting new application from dashboard modal - initiating cancellation`
    );
    datadogRum.addAction(
      `User confirmed starting new application from dashboard modal - initiating cancellation`
    );
    cancelLoanApplicationAPI()
      .then(() => {
        loanApplicationActions.reset();
        reset();
        posthog.reset(true);
        datadogRum.clearUser();
        switchDisplayMode("loan-application");
        posthog.capture(
          `User started new application after cancellation from dashboard modal`
        );
        datadogRum.addAction(
          `User started new application after cancellation from dashboard modal`
        );
      })
      .catch((err) => {
        console.error(
          "Failed to cancel existing loan application from modal:",
          err
        );
        posthog.capture(
          `Failed to cancel existing loan application from dashboard modal`,
          { error: String(err) }
        );
        loanApplicationActions.reset();
        reset();
        posthog.reset(true);
        datadogRum.clearUser();
        switchDisplayMode("loan-application");
      })
      .finally(() => {
        setIsStartNewConfirmModalOpen(false);
      });
  };

  const handleLoanApplicationFlow = (actionContext: string) => {
    if (actionContext === "Start new application") {
      if (activeLoanApplication?.id) {
        posthog.capture(
          `User clicked on button ${actionContext} from dashboard - showing confirmation modal`
        );
        datadogRum.addAction(
          `User clicked on button ${actionContext} from dashboard - showing confirmation modal`
        );
        setIsStartNewConfirmModalOpen(true);
      } else {
        posthog.capture(
          `User clicked on button ${actionContext} from dashboard - no existing app conflict, proceeding`
        );
        datadogRum.addAction(
          `User clicked on button ${actionContext} from dashboard - no existing app conflict, proceeding`
        );
        loanApplicationActions.reset();
        reset();
        posthog.reset(true);
        datadogRum.clearUser();
        switchDisplayMode("loan-application");
      }
    } else if (actionContext === "Continue loan application") {
      posthog.capture(`User clicked on button ${actionContext} from dashboard`);
      datadogRum.addAction(
        `User clicked on button ${actionContext} from dashboard`
      );
      switchDisplayMode("loan-application");
    }
  };

  const locLoanTypes = [
    LoanProductType.LOC_INVOICE_DISCOUNTING,
    LoanProductType.LOC_PAYABLE_FINANCING,
  ];

  const [termSearchQuery, setTermSearchQuery] = useState<string>("");
  const [termStatusFilterLabel, setTermStatusFilterLabel] =
    useState<string>("");
  const isInitialFilterMount = useRef(true);

  const excludedStatusLabels = ["Cancelled", "Expired"];

  const uniqueStatusLabels = useMemo(() => {
    return Object.keys(StatusLabelReverseMapping)
      .filter((label) => !excludedStatusLabels.includes(label))
      .sort();
  }, []);

  const statusOptions: SelectOption[] = useMemo(() => {
    return uniqueStatusLabels.map((label) => ({
      value: label,
      label: label,
      color: StatusLabelReverseMapping[label]?.color,
    }));
  }, [uniqueStatusLabels]);

  const computeStatusFilters = useMemo(() => {
    if (
      !termStatusFilterLabel ||
      !StatusLabelReverseMapping[termStatusFilterLabel]
    ) {
      return { statuses: [], caseStatuses: [] };
    }

    const mapping = StatusLabelReverseMapping[termStatusFilterLabel];
    return {
      statuses: mapping.statuses,
      caseStatuses: mapping.caseStatuses,
    };
  }, [termStatusFilterLabel]);

  const filteredTermLoans = useMemo(() => {
    const termLoans =
      loans
        ?.filter((loan) => loan.status !== LoanApplicationStatus.STARTED)
        ?.filter((loan) => !locLoanTypes.includes(loan.loanType)) ?? [];
    return sortLoansActiveFirst(termLoans);
  }, [loans]);

  const sortedLocLoans = useMemo(() => {
    const locLoans =
      loans
        ?.filter((loan) => loan.status !== LoanApplicationStatus.STARTED)
        ?.filter((loan) => locLoanTypes.includes(loan.loanType)) ?? [];
    return sortLoansActiveFirst(locLoans);
  }, [loans]);

  const hasActiveLoans =
    activeLoanContractsSummary.creditLineFacilitiesCount +
      activeLoanContractsSummary.termLoanFacilitiesCount >
    0;

  const totalFacilitiesCount =
    activeLoanContractsSummary.creditLineFacilitiesCount +
    activeLoanContractsSummary.termLoanFacilitiesCount;

  const showProgressCard = !hasActiveLoans && Boolean(activeLoanApplication);

  const showOverdueCard =
    activeLoanContractsSummary.overdueInstallmentAmount > 0;
  const showUpcomingCard =
    !showOverdueCard &&
    activeLoanContractsSummary.upcomingInstallmentsAmount > 0;

  const totalLoans = filteredTermLoans.length;

  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    const params: Record<string, any> = {};
    const search = termSearchQuery.trim();
    if (search) params.searchText = search;

    const { statuses, caseStatuses } = computeStatusFilters;

    if (statuses.length > 0) params.status = statuses;
    if (caseStatuses.length > 0) params.caseStatus = caseStatuses;

    refetch(params);
  }, [termSearchQuery, computeStatusFilters, refetch]);

  useEffect(() => {
    if (prevFilterLoadingRef.current && !filterLoading) {
      termFacilitiesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    prevFilterLoadingRef.current = filterLoading;
  }, [filterLoading]);

  const renderAmountWithCurrency = (
    amount: number,
    _size: "large" | "small" = "large",
    colorClass = "cx-text-text-primary"
  ) => {
    return (
      <span className={`cx-flex cx-items-center cx-relative ${colorClass}`}>
        <div className="cx-font-aed-font"> D </div>
        <span className="cx-ml-1">{formatAmount(amount)}</span>
      </span>
    );
  };

  return (
    <div className="cx-grow cx-flex cx-flex-col cx-items-center cx-background-radial-gradient cx-w-full cx-p-4 sm:cx-p-8">
      {loading || isLoading ? (
        <div className="cx-animate-spin cx-text-brand-primary-regular cx-w-10 cx-h-10">
          <ButtonLoaderIcon width="100%" height="100%" />
        </div>
      ) : (
        <>
          <div className="cx-w-full cx-max-w-[960px] cx-py-3 cx-relative">
            {/* header */}
            <div className="cx-flex cx-flex-col sm:cx-flex-row sm:cx-items-center sm:cx-justify-between cx-gap-3 cx-mb-6">
              <div className="cx-grow cx-font-bold cx-text-2xl cx-text-text-primary">
                {showProgressCard ? "Facility overview" : "Portfolio overview"}
              </div>
              <div className="cx-flex cx-flex-wrap cx-items-center cx-gap-3 sm:cx-gap-6">
                {allowNewLoan?.existing && !showProgressCard && (
                  <button
                    type="button"
                    className="cx-px-3 cx-py-2 cx-bg-white cx-border-[1.5px] cx-border-neutral-light cx-text-brand-primary-regular cx-rounded-full cx-inline-flex cx-items-center cx-justify-center cx-gap-2 cx-text-sm cx-font-semibold cx-leading-none cx-w-fit"
                    onClick={() =>
                      handleLoanApplicationFlow("Continue loan application")
                    }
                  >
                    <PenIcon className="cx-inline-block cx-w-4 cx-h-4" />
                    <span className="cx-inline-block">
                      Continue application
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className="cx-px-3 cx-py-2 cx-bg-brand-primary-regular cx-text-white cx-rounded-full cx-inline-flex cx-items-center cx-justify-center cx-gap-2 cx-text-sm cx-font-semibold cx-leading-none cx-w-fit"
                  onClick={() =>
                    handleLoanApplicationFlow("Start new application")
                  }
                >
                  <PlusIcon className="cx-inline-block" />
                  <span className="cx-inline-block">Request new facility</span>
                </button>
              </div>
            </div>

            {hasActiveLoans && (
              <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 lg:cx-grid-cols-3 cx-gap-6">
                {activeLoanContractsSummary.totalApprovedFacilityAmount > 0 && (
                  <PortfolioCard
                    id="approved"
                    icon={<CoinsIcon className="cx-w-11 cx-h-11" />}
                    title="Total approved facility"
                    value={totalFacilitiesCount}
                    footerLayout="row"
                    breakdown={
                      [
                        activeLoanContractsSummary.creditLineFacilitiesCount >
                          0 &&
                          activeLoanContractsSummary.creditLineTotalApprovedAmount >
                            0 && {
                            label: `${
                              activeLoanContractsSummary.creditLineFacilitiesCount
                            } credit ${
                              activeLoanContractsSummary.creditLineFacilitiesCount ===
                              1
                                ? "facility"
                                : "facilities"
                            }`,
                            amount: renderAmountWithCurrency(
                              activeLoanContractsSummary.creditLineTotalApprovedAmount,
                              "small"
                            ),
                          },
                        activeLoanContractsSummary.termLoanFacilitiesCount >
                          0 &&
                          activeLoanContractsSummary.termLoanTotalApprovedAmount >
                            0 && {
                            label: `${
                              activeLoanContractsSummary.termLoanFacilitiesCount
                            } term ${
                              activeLoanContractsSummary.termLoanFacilitiesCount ===
                              1
                                ? "facility"
                                : "facilities"
                            }`,
                            amount: renderAmountWithCurrency(
                              activeLoanContractsSummary.termLoanTotalApprovedAmount,
                              "small"
                            ),
                          },
                      ].filter(Boolean) as {
                        label: string;
                        amount: React.ReactNode;
                      }[]
                    }
                  />
                )}
                {activeLoanContractsSummary.totalOutstandingAmount > 0 && (
                  <PortfolioCard
                    id="outstanding"
                    icon={<HandCoinsIcon className="cx-w-11 cx-h-11" />}
                    title="Total outstanding"
                    value={renderAmountWithCurrency(
                      activeLoanContractsSummary.totalOutstandingAmount
                    )}
                    footerLayout="row"
                    breakdown={
                      [
                        activeLoanContractsSummary.creditLineFacilitiesCount >
                          0 &&
                          activeLoanContractsSummary.creditLineTotalOutstandingAmount >
                            0 && {
                            label: `${
                              activeLoanContractsSummary.creditLineFacilitiesCount
                            } credit ${
                              activeLoanContractsSummary.creditLineFacilitiesCount ===
                              1
                                ? "facility"
                                : "facilities"
                            }`,
                            amount: renderAmountWithCurrency(
                              activeLoanContractsSummary.creditLineTotalOutstandingAmount,
                              "small"
                            ),
                          },
                        activeLoanContractsSummary.termLoanFacilitiesCount >
                          0 &&
                          activeLoanContractsSummary.termLoanTotalOutstandingAmount >
                            0 && {
                            label: `${
                              activeLoanContractsSummary.termLoanFacilitiesCount
                            } term ${
                              activeLoanContractsSummary.termLoanFacilitiesCount ===
                              1
                                ? "facility"
                                : "facilities"
                            }`,
                            amount: renderAmountWithCurrency(
                              activeLoanContractsSummary.termLoanTotalOutstandingAmount,
                              "small"
                            ),
                          },
                      ].filter(Boolean) as {
                        label: string;
                        amount: React.ReactNode;
                      }[]
                    }
                  />
                )}
                {showOverdueCard && (
                  <PortfolioCard
                    id="overdue"
                    icon={
                      <HeavyWarningIcon className="cx-w-11 cx-h-11 cx-text-red-800" />
                    }
                    title="Overdue amount"
                    variant="danger"
                    value={renderAmountWithCurrency(
                      activeLoanContractsSummary.overdueInstallmentAmount,
                      "large",
                      "cx-text-red-800"
                    )}
                    footerLayout="row"
                    breakdown={
                      [
                        activeLoanContractsSummary.overdueDaysCount > 0 && {
                          colorClass: "cx-bg-red-600",
                          showDot: true,
                          label: "Days past due",
                          amountClassName: "cx-text-text-primary",
                          amount: `${
                            activeLoanContractsSummary.overdueDaysCount
                          } ${
                            activeLoanContractsSummary.overdueDaysCount === 1
                              ? "day"
                              : "days"
                          }`,
                        },
                        activeLoanContractsSummary.overdueInstallmentLateFee >
                          0 && {
                          colorClass: "cx-bg-red-600",
                          showDot: true,
                          label: "Late fee incurred",
                          amount: renderAmountWithCurrency(
                            activeLoanContractsSummary.overdueInstallmentLateFee,
                            "small",
                            "cx-text-red-800"
                          ),
                        },
                      ].filter(Boolean) as {
                        colorClass: string;
                        showDot: boolean;
                        label: string;
                        amount: React.ReactNode;
                      }[]
                    }
                  />
                )}
                {showUpcomingCard && (
                  <PortfolioCard
                    id="upcoming"
                    icon={
                      <HeavyWarningIcon className="cx-w-11 cx-h-11 cx-text-orange-600" />
                    }
                    title="Upcoming amount"
                    variant="warning"
                    value={renderAmountWithCurrency(
                      activeLoanContractsSummary.upcomingInstallmentsAmount,
                      "large",
                      "cx-text-orange-600"
                    )}
                    paymentDate={
                      activeLoanContractsSummary.upcomingPaymentDate
                        ? {
                            label: "Upcoming payment",
                            date: formatISOString(
                              activeLoanContractsSummary.upcomingPaymentDate
                            ),
                          }
                        : undefined
                    }
                    dots={
                      [
                        activeLoanContractsSummary.upcomingInvoicesCount >
                          0 && {
                          colorClass: "cx-bg-yellow-400",
                          label: `${
                            activeLoanContractsSummary.upcomingInvoicesCount
                          } ${
                            activeLoanContractsSummary.upcomingInvoicesCount ===
                            1
                              ? "invoice"
                              : "invoices"
                          }`,
                        },
                        activeLoanContractsSummary.upcomingInstallmentsCount >
                          0 && {
                          colorClass: "cx-bg-orange-500",
                          label: `${
                            activeLoanContractsSummary.upcomingInstallmentsCount
                          } ${
                            activeLoanContractsSummary.upcomingInstallmentsCount ===
                            1
                              ? "installment"
                              : "installments"
                          }`,
                        },
                      ].filter(Boolean) as {
                        colorClass: string;
                        label: string;
                      }[]
                    }
                  />
                )}
              </div>
            )}
          </div>

          {showProgressCard && (
            <Card>
              <div className="cx-w-full sm:cx-flex cx-flex-row-reverse cx-px-6 cx-pb-4 cx-border-b-0.5 cx-border-neutral-light">
                <div className="cx-w-full sm:cx-flex cx-flex-row cx-items-center cx-justify-between cx-gap-x-2">
                  <div className={"cx-flex cx-items-center"}>
                    <div
                      className={
                        "cx-text-text-primary cx-font-semibold cx-text-lg cx-mr-4"
                      }
                    >
                      Application in process
                    </div>
                    <div
                      className={
                        "cx-text-base cx-text-text-brand cx-font-semibold"
                      }
                    >
                      {currentStep[0]} steps completed!
                    </div>
                  </div>
                  <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
                    {`Loan #${activeLoanApplication?.externalId}`}
                  </div>
                </div>
              </div>
              <div>
                <DashboardProgressBar currentStep={currentStep} steps={steps} />
              </div>
              <div
                className={
                  "cx-flex cx-flex-col cx-content-center cx-items-center cx-pt-8 cx-pb-2 cx-gap-5"
                }
              >
                <EmptyApplicationIcon />
                <div
                  className={
                    "cx-font-normal cx-text-text-secondary cx-text-center"
                  }
                >
                  Loan info will show here after you apply and get approved.
                </div>
              </div>
              <div
                className={
                  "cx-text-base cx-text-text-primary cx-font-semibold cx-text-center"
                }
              >
                Only {steps.length - 1 - currentStep[0]} steps left!
              </div>
              {allowNewLoan?.existing && (
                <div className="cx-flex cx-justify-center cx-py-4">
                  <Button
                    slim
                    label={
                      <div className="cx-flex cx-items-center">
                        <PenIcon className="cx-mr-2" />
                        <div>Continue loan application</div>
                      </div>
                    }
                    onClick={() =>
                      handleLoanApplicationFlow("Continue loan application")
                    }
                    className="cx-bg-indigo-600 hover:cx-bg-indigo-700 cx-text-white cx-rounded-full cx-border-2 cx-border-indigo-300 cx-px-6 cx-py-2 cx-animate-pulse-glow"
                  />
                </div>
              )}
            </Card>
          )}

          {sortedLocLoans.map((loan, index) => (
            <LoanCard
              key={`loc-loan-${index}`}
              loan={loan}
              index={index}
              title={
                <div className="cx-w-full cx-flex cx-items-center cx-justify-between cx-gap-2 cx-p-2 sm:cx-p-4 cx-min-w-0">
                  <div className="cx-min-w-0 cx-break-words">
                    Credit facilities
                  </div>
                  <div
                    className={`cx-flex cx-items-center cx-shrink-0 cx-px-2 sm:cx-px-3 cx-py-1 cx-text-xs sm:cx-text-sm cx-font-medium cx-leading-5 cx-rounded-full ${utilisationColors.bg} cx-border ${utilisationColors.border} ${utilisationColors.text} cx-text-center cx-whitespace-nowrap`}
                  >
                    <span
                      className={`cx-inline-block cx-w-2 cx-h-2 cx-rounded-full ${utilisationColors.dot} cx-mr-2`}
                    />
                    Utilisation: {utilisationPercentage}%
                  </div>
                </div>
              }
            />
          ))}

          {(totalLoans > 0 || termSearchQuery || termStatusFilterLabel) && (
            <>
              <div
                ref={termFacilitiesRef}
                className="cx-flex cx-flex-col sm:cx-flex-row sm:cx-items-center sm:cx-justify-between cx-gap-3 cx-w-full cx-max-w-[960px] cx-py-6 cx-relative"
              >
                <span className="cx-grow cx-font-bold cx-text-2xl cx-text-text-primary">
                  Term facilities
                </span>
                <div className="cx-flex cx-items-center cx-gap-x-2 cx-w-full sm:cx-w-auto sm:cx-ml-auto">
                  {filterLoading && (
                    <span
                      className="cx-flex cx-items-center cx-text-sm cx-text-neutral-medium"
                      aria-hidden
                    >
                      <ButtonLoaderIcon className="cx-w-4 cx-h-4 cx-animate-spin" />
                    </span>
                  )}
                  <div className="cx-relative cx-flex-1 sm:cx-flex-none">
                    <span className="cx-absolute cx-left-2 cx-top-1/2 -cx-translate-y-1/2">
                      <SearchIcon className="cx-w-4 cx-h-4 cx-text-neutral-medium" />
                    </span>
                    <input
                      className="cx-border cx-border-neutral-light cx-rounded-btn cx-px-3 cx-pl-8 cx-py-2 cx-text-sm cx-text-black cx-font-normal cx-w-full sm:cx-w-56 cx-min-h-9"
                      placeholder="Search by Product or ID#"
                      value={termSearchQuery}
                      onChange={(e) => setTermSearchQuery(e.target.value)}
                      disabled={filterLoading}
                    />
                  </div>
                  <Select
                    options={statusOptions}
                    value={termStatusFilterLabel}
                    onChange={setTermStatusFilterLabel}
                    placeholder="Status"
                    defaultLabel="All statuses"
                    icon={
                      <StatusFilterIcon className="cx-w-4 cx-h-4 cx-text-neutral-medium" />
                    }
                  />
                </div>
              </div>

              {filterLoading ? (
                <div className="cx-w-full cx-max-w-[960px] cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-12 cx-px-4 cx-bg-white/50 cx-rounded-lg cx-border cx-border-neutral-light">
                  <ButtonLoaderIcon className="cx-w-10 cx-h-10 cx-animate-spin cx-text-brand-primary-regular" />
                  <span className="cx-mt-3 cx-text-sm cx-text-text-secondary">
                    Loading term facilities…
                  </span>
                </div>
              ) : (
                filteredTermLoans.length > 0 &&
                filteredTermLoans.map((loan, index) => (
                  <LoanCard
                    key={`other-loan-${index}`}
                    loan={loan}
                    index={index}
                    showTitle={false}
                  />
                ))
              )}
            </>
          )}

          <Modal
            className="cx-w-full"
            title="Early settlement"
            isOpen={isOpenModal}
            onClose={() => setIsOpenModal(false)}
          >
            <div>
              <div className="cx-w-full cx-text-text-secondary cx-text-base">
                Account Name
              </div>
              <div className="cx-w-full cx-text-text-secondary cx-text-base">
                IBAN
              </div>
              <div className="cx-w-full cx-text-text-secondary cx-text-base">
                Account Number
              </div>
              <div className="cx-w-full cx-text-text-secondary cx-text-base">
                Bank Name
              </div>
              <div className="cx-w-full cx-text-text-secondary cx-text-base">
                Bank Address
              </div>
            </div>
            <div className="cx-py-6 cx-w-full cx-text-text-secondary cx-text-base">
              Terms and conditions goes herw
            </div>
            <div className="cx-flex cx-flex-col cx-gap-y-4">
              <Button slim label="Done" onClick={() => setIsOpenModal(false)} />
              <Button
                slim
                outlined
                label="Cancel"
                onClick={() => setIsOpenModal(false)}
              />
            </div>
          </Modal>
          <StartNewApplicationConfirmModal
            isOpen={isStartNewConfirmModalOpen}
            onClose={() => setIsStartNewConfirmModalOpen(false)}
            onConfirm={handleConfirmStartNewApplication}
          />
        </>
      )}
    </div>
  );
};

export default LoanDashboardForm;
